// Shared client/server WebSocket protocol. Imported by both the Worker and web/.

import type { Lang } from "./languages";
import type { ThemeChoice, ThemeId } from "./themes";

/**
 * Path the game is mounted on: it is served as part of the WorkBrawl hub at
 * https://workbrawl.com/wordsbrawl/. Static files live in public/wordsbrawl/ to match.
 */
export const BASE_PATH = "/wordsbrawl";

export type Cell = [number, number];
/**
 * waiting: timed room before someone presses Start (board hidden). countdown: 3-2-1 before
 * the clock starts (board still hidden). playing / finished: as always. Rooms without a
 * time limit skip the first two.
 */
export type RoomStatus = "waiting" | "countdown" | "playing" | "finished";

/** Round lengths the room creator can pick, in seconds (same as the other WorkBrawl titles). */
export const DURATIONS = [120, 180, 300] as const;
export type Duration = (typeof DURATIONS)[number];
export const DEFAULT_DURATION: Duration = 180;
/** 3-2-1 between pressing Start and the clock running. */
export const COUNTDOWN_MS = 3000;

export function isDuration(value: unknown): value is Duration {
  return (DURATIONS as readonly unknown[]).includes(value);
}

/**
 * Rounds per series, picked when the room is created. After the last one everyone sees the
 * standings of the whole series; with 1 (the default) every round stands on its own.
 */
export const ROUND_COUNTS = [1, 3, 5] as const;
export type RoundCount = (typeof ROUND_COUNTS)[number];
export const DEFAULT_ROUND_COUNT: RoundCount = 1;

export function isRoundCount(value: unknown): value is RoundCount {
  return (ROUND_COUNTS as readonly unknown[]).includes(value);
}

/** One person's line in the series standings. */
export interface SeriesStanding {
  id: string;
  name: string;
  color: string;
  /** Points in each finished round of the series, in order (0 for rounds they missed). */
  scores: number[];
  total: number;
}

/** Where the room is in its series of rounds. */
export interface SeriesInfo {
  /** Rounds in the series (ROUND_COUNTS). */
  length: RoundCount;
  /** 1-based number of the current round within the series. */
  index: number;
  /** Totals over the finished rounds of the series, best first. */
  standings: SeriesStanding[];
}

export interface PlayerInfo {
  id: string;
  name: string;
  color: string;
  score: number;
  online: boolean;
}

export interface FoundInfo {
  word: string;
  start: Cell;
  end: Cell;
  by: string;
}

export type ClientMsg =
  | { type: "select"; start: Cell; end: Cell }
  | { type: "drag"; start: Cell; end: Cell }
  | { type: "drag_end" }
  | { type: "rematch"; theme?: ThemeChoice } // omitted = random
  | { type: "change_theme"; theme: ThemeChoice } // before the round starts (untimed: before the first find)
  | { type: "start" } // timed rooms: begins the countdown
  | { type: "ping" };

/** JSON body of POST /api/rooms. */
export interface CreateRoomRequest {
  theme?: ThemeChoice; // omitted = random
  lang?: Lang; // word language; omitted = DEFAULT_LANG (English)
  duration?: Duration; // round length in seconds; omitted = no time limit
  rounds?: RoundCount; // rounds per series; omitted = DEFAULT_ROUND_COUNT
  turnstileToken: string;
}

/** JSON body of POST /api/rooms/:code/verify (join by code from the home screen). */
export interface VerifyRoomRequest {
  turnstileToken: string;
}

/** Response of GET /api/config. */
export interface ClientConfig {
  turnstileSiteKey: string;
}

/** Error codes returned as `{ error }` by the HTTP API. */
export type ApiErrorCode = "INVALID_REQUEST" | "TURNSTILE_FAILED" | "ROOM_NOT_FOUND";

export type ServerMsg =
  | {
      type: "state";
      you: string;
      round: number;
      size: number;
      grid: string[][];
      words: string[];
      /** Theme id; clients translate it. */
      theme: ThemeId;
      /** Language of the words (fixed per room). */
      lang: Lang;
      status: RoomStatus;
      /** Round length in seconds; null in rooms without a time limit. */
      duration: Duration | null;
      /** Milliseconds left in the countdown or the round; null when no clock is running. */
      remainingMs: number | null;
      found: FoundInfo[];
      players: PlayerInfo[];
      series: SeriesInfo;
    }
  | { type: "player_joined" | "player_updated"; player: PlayerInfo }
  | { type: "player_left"; id: string }
  | { type: "found"; word: string; start: Cell; end: Cell; by: string; score: number }
  | { type: "miss" }
  | { type: "drag"; by: string; start: Cell; end: Cell }
  | { type: "drag_end"; by: string }
  | { type: "game_over"; ranking: { id: string; name: string; score: number }[]; series: SeriesInfo }
  | { type: "error"; code: ErrorCode; message: string }
  | { type: "pong" };

/**
 * Exact keep-alive frames. The room registers them as a WebSocket auto-response, so
 * Cloudflare answers pings at the edge WITHOUT waking the Durable Object: an idle room
 * with open tabs stays hibernated (no billable duration). The client must send PING_FRAME
 * byte for byte, or the ping falls through to the room and wakes it.
 */
export const PING_FRAME = '{"type":"ping"}';
export const PONG_FRAME = '{"type":"pong"}';

export type ErrorCode = "ROOM_FULL" | "ROOM_NOT_FOUND" | "BAD_MESSAGE" | "THEME_LOCKED";

export const ROOM_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const ROOM_CODE_LENGTH = 5;
export const NAME_MAX_LENGTH = 20;
/** Players per room. PLAYER_COLORS (room.ts) must have at least this many colors. */
export const MAX_PLAYERS = 12;

const ROOM_CODE_RE = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidRoomCode(code: string): boolean {
  return ROOM_CODE_RE.test(code);
}

export function isValidPlayerId(id: string): boolean {
  return UUID_RE.test(id);
}

/**
 * Trims and collapses whitespace. Returns null when the name is empty, longer than
 * NAME_MAX_LENGTH characters, or contains HTML-ish or control characters.
 */
export function sanitizeName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, " ");
  const length = [...name].length;
  if (length < 1 || length > NAME_MAX_LENGTH) return null;
  if (/[<>&"'`\p{Cc}]/u.test(name)) return null;
  return name;
}
